import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createDict, listDict, DictEntry } from '../api/dict';
import { alertApprovalIfPending, ApiError } from '../api/request';
import { useCan } from '../store';
import { alertDialog } from '../utils/dialog';
import { selLayoutCls, withCurrent } from '../utils/searchOptions';
import SearchSelect from './SearchSelect';

// 受控字段公共下拉 (2026-08-05 ★全局方案第一版):
// - 选项来自「字典维护」对应 category (材-03 工艺/表面 = craft/surface, 设-01 光源/波段 = light_source/band);
// - 末项「+ 其他 (新增)」仅 admin 可见 (2026-08-19 字典写口收回 admin-only):
//   展开输入框 → 提交字典新增, 走 changereq 审批, 通过后全局可选;
//   普通用户只能选现有字典项, 需要新值请联系管理员;
// - 历史自由文本值不在字典时保留为禁用提示项, 不丢数据。

const OTHER = '__other__';

// 模块级缓存: category → entries (与 capTypes/schemaFields 同模式)
const cache = new Map<string, DictEntry[]>();
const inflight = new Map<string, Promise<DictEntry[]>>();

/** cacheKey 分域类目按 (类目, 域) 各缓存一份 —— 同一类目在不同域下是不同清单. */
function cacheKey(category: string, scope?: string): string {
    return scope ? `${category}\u0000${scope}` : category;
}

function fetchDictCached(
    category: string,
    scope?: string,
): Promise<DictEntry[]> {
    const key = cacheKey(category, scope);
    const hit = cache.get(key);
    if (hit) return Promise.resolve(hit);
    let p = inflight.get(key);
    if (!p) {
        p = listDict(category, scope ? { scope } : undefined)
            .then((r) => {
                cache.set(key, r || []);
                return r || [];
            })
            .catch((e) => {
                inflight.delete(key); // 失败不缓存
                throw e;
            });
        inflight.set(category, p);
    }
    return p;
}

/** 字典维护页写操作后可调用, 让全站受控下拉重新拉取. */
export function invalidateDictSelectCache(category?: string) {
    if (category) {
        // 分域类目按 (类目, 域) 分开缓存, 清一个类目要把它所有域一起清
        Array.from(cache.keys())
            .filter((k) => k === category || k.startsWith(`${category}\u0000`))
            .forEach((k) => cache.delete(k));
        Array.from(inflight.keys())
            .filter((k) => k === category || k.startsWith(`${category}\u0000`))
            .forEach((k) => inflight.delete(k));
    } else {
        cache.clear();
        inflight.clear();
    }
}

/** 某类字典的 code → label 展示映射 (共享 DictSelect 的模块级缓存); 未加载/未命中时调用方回退显示 code. */
export function useDictLabels(category: string): Record<string, string> {
    const [labels, setLabels] = useState<Record<string, string>>({});
    useEffect(() => {
        let alive = true;
        fetchDictCached(category)
            .then((entries) => {
                if (alive)
                    setLabels(
                        Object.fromEntries(
                            entries.map((e) => [e.code, e.label]),
                        ),
                    );
            })
            .catch(() => undefined);
        return () => {
            alive = false;
        };
    }, [category]);
    return labels;
}

/**
 * 字典 code → 展示名的取值器. **列表/详情展示一律走这里, 不直接渲染 code**
 * (光源 blue 要显示"蓝光", 动作 eng 要显示"雕刻").
 * 字典未加载完或该 code 不在字典 (历史自由文本值) 时回退原 code, 不显示空白.
 */
export function useDictText(
    category: string,
): (code?: string, fallback?: string) => string {
    const labels = useDictLabels(category);
    // useCallback 稳定函数标识 (仅字典加载完成时变化), 可安全放进调用方的 useMemo/useEffect 依赖
    return useCallback(
        (code, fallback = '—') => (code ? labels[code] || code : fallback),
        [labels],
    );
}

interface Props {
    /** 字典类别: craft / surface / light_source / band ... */
    category: string;
    value: string;
    onChange: (v: string) => void;
    className?: string;
    /** 空值项文案 */
    emptyLabel?: string;
    disabled?: boolean;
    /** 分域类目的上下文 (如机型 device_family 按工艺框架分域); 非分域类目不传 */
    scope?: string;
    /** 选项只显示中文名, 不带 "(code)" 后缀 (默认 false = 显示, 维护人员靠 code 区分同名项) */
    hideCode?: boolean;
}

function DictSelect({
    category,
    value,
    onChange,
    className = 'sel',
    emptyLabel = '(未选)',
    disabled,
    scope,
    hideCode = false,
}: Props) {
    const canCreateDict = useCan('dict', 'create');
    const [entries, setEntries] = useState<DictEntry[]>(
        cache.get(cacheKey(category, scope)) || [],
    );
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        fetchDictCached(category, scope)
            .then((r) => {
                if (alive) setEntries(r);
            })
            .catch(() => {
                if (alive) setEntries([]);
            });
        return () => {
            alive = false;
        };
    }, [category, scope]);

    const options = useMemo(() => {
        const opts = entries.map((d) => ({
            value: d.code,
            label:
                d.label && d.label !== d.code
                    ? hideCode
                        ? d.label
                        : `${d.label} (${d.code})`
                    : d.code,
        }));
        // 历史自由文本值: 不在字典 → 保留为提示项 (不丢数据, 引导换选)
        const withLegacy = withCurrent(
            opts,
            value,
            `${value} (旧值, 已不在字典)`,
        );
        // 字典写口 2026-08-19 起 admin-only, 非 admin 不给"新增"入口 (点了只会 403)
        return canCreateDict
            ? [...withLegacy, { value: OTHER, label: '+ 其他 (新增送审)…' }]
            : withLegacy;
    }, [entries, hideCode, value, canCreateDict]);

    const submitNew = async () => {
        const v = draft.trim();
        if (!v) return;
        setBusy(true);
        try {
            const res = await createDict(category, {
                code: v,
                label: v,
                sort: 99,
                ...(scope ? { scopeKey: scope } : {}),
            });
            invalidateDictSelectCache(category);
            const fresh = await fetchDictCached(category, scope);
            setEntries(fresh);
            // admin 即时生效 → 回填选中; 普通用户送审 → 值先暂填字段, 字典侧待审批
            onChange(v);
            setAdding(false);
            setDraft('');
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(
                `新增失败: ${e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)}`,
            );
        } finally {
            setBusy(false);
        }
    };

    if (adding) {
        return (
            <span className="flex items-center gap-1">
                <input
                    className="inp flex-1"
                    autoFocus
                    placeholder="新值 (提交后进字典, 走审批)"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            submitNew();
                        }
                        if (e.key === 'Escape') setAdding(false);
                    }}
                />
                <button
                    type="button"
                    className="btn-ghost text-xs px-2"
                    disabled={busy || !draft.trim()}
                    onClick={submitNew}
                >
                    {busy ? '…' : '✓'}
                </button>
                <button
                    type="button"
                    className="btn-ghost text-xs px-2"
                    disabled={busy}
                    onClick={() => setAdding(false)}
                >
                    ✕
                </button>
            </span>
        );
    }

    return (
        <SearchSelect
            className={selLayoutCls(className)}
            value={value}
            disabled={disabled}
            onChange={(v) => {
                if (v === OTHER) {
                    setAdding(true);
                    return;
                }
                onChange(v);
            }}
            placeholder={emptyLabel}
            options={options}
            clearable
        />
    );
}

export default DictSelect;
